import { createStyles, Text, Box, Divider, UnstyledButton, Collapse } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { Game } from '@betterpool/api-types/games-service';
import useTranslation from 'next-translate/useTranslation';

import dt from '~/testing';

import { formatGameItemDate } from './utils';

const useStyles = createStyles((theme) => ({
  divider: {
    borderTopColor: theme.colors.gray[2],
  },
  actionButton: {
    position: 'absolute',
    right: theme.spacing.md,
    top: theme.spacing.md,
  },
}));

const ITEM_COUNT = 1;

type GameListProps = {
  games: Game[];
};

function GameList({ games = [] }: GameListProps) {
  const [opened, { toggle }] = useDisclosure(false);
  const { classes } = useStyles();
  const { t } = useTranslation('contest');

  if (games.length === 0) {
    return (
      <Text c="gray.5" data-test-id={dt.contestDetail.section.about.game.item.nogames}>
        {t('detail.about.game.modal.nogames')}
      </Text>
    );
  }

  return (
    <>
      {games.slice(0, 1).map((game) => (
        <Box
          data-test-id={dt.contestDetail.section.about.game.item.container}
          px="md"
          key={game.id}
        >
          <Text data-test-id={dt.contestDetail.section.about.game.item.date}>
            {formatGameItemDate(game.start_date)}
          </Text>
          <Text data-test-id={dt.contestDetail.section.about.game.item.name}>{game.name}</Text>
        </Box>
      ))}
      {games.length > ITEM_COUNT &&
        games.slice(1).map((game) => (
          <Collapse key={game.id} in={opened}>
            <Divider className={classes.divider} my="xs" />
            <Box
              data-test-id={dt.contestDetail.section.about.game.item.container}
              px="md"
              key={game.id}
            >
              <Text data-test-id={dt.contestDetail.section.about.game.item.date}>
                {formatGameItemDate(game.start_date)}
              </Text>
              <Text data-test-id={dt.contestDetail.section.about.game.item.name}>{game.name}</Text>
            </Box>
          </Collapse>
        ))}
      {games.length > ITEM_COUNT && (
        <UnstyledButton
          data-test-id={dt.contestDetail.section.about.game.item.collapse}
          onClick={toggle}
          className={classes.actionButton}
        >
          <Text color="cyan.5" size="sm">
            {opened
              ? t('detail.about.game.modal.collapse.hide')
              : t('detail.about.game.modal.collapse.show')}
          </Text>
        </UnstyledButton>
      )}
    </>
  );
}

export default GameList;
